import { Link } from 'react-router-dom'
import imgCouplePlanning from '../../Assets/landing/features/couple-planning.webp'
import imgSchedule from '../../Assets/landing/features/schedule.png'
import imgRsvp from '../../Assets/landing/features/rsvp.png'
import imgAppPreview from '../../Assets/app_1.webp'

/**
 * Blog metadata + content, oldest first. The index page and sitemap use
 * the metadata; BlogPost.js renders the `sections` JSX.
 */
const BLOG_POSTS = [
    {
        slug: 'best-wedding-planning-apps',
        title: 'Which Wedding Planning App is Best? Top 5 Apps Compared',
        seoTitle: 'Which Wedding Planning App is Best? Top 5 Compared | First Dance',
        seoDescription:
            'We compare the top 5 wedding planning apps – First Dance, Zola, The Knot, Bridebook and Hitched – on features, pricing and user experience for UK couples.',
        excerpt:
            'First Dance, Zola, The Knot, Bridebook and Hitched compared on features, pricing and user experience.',
        date: '2026-05-12',
        dateLabel: '12 May 2026',
        readTime: '5 min read',
        image: imgAppPreview,
        imageAlt: 'First Dance wedding planning app preview',
        intro:
            'Choosing the right wedding planning app can transform your wedding journey. We’ve analysed five popular platforms — First Dance, Zola, The Knot, Bridebook and Hitched — to help you make an informed decision based on features, pricing and user experience.',
        sections: [
            {
                heading: 'First Dance',
                body: (
                    <>
                        <p>
                            <strong>Overview:</strong> A UK-based wedding planning app built around one idea —
                            everything for your big day in one place, made fun. The free plan covers the
                            essentials, with premium plans adding your own wedding website, digital RSVPs and
                            a guest photo album. See the full breakdown on the{' '}
                            <Link to="/pricing">pricing page</Link>.
                        </p>
                        <p>
                            <strong>Key features:</strong>
                        </p>
                        <ul>
                            <li>
                                <Link to="/features/guest-list">Guest list</Link> with plus-ones, day/evening
                                invitations and dietary notes
                            </li>
                            <li>
                                <Link to="/features/rsvp">Digital RSVPs</Link> sent over WhatsApp, with meal
                                choices collected automatically
                            </li>
                            <li>
                                Interactive <Link to="/features/table-planner">table &amp; seating planner</Link>
                            </li>
                            <li>
                                Personalised <Link to="/features/wedding-website">wedding website</Link> with
                                QR code and <Link to="/features/guest-photos">guest photo uploads</Link>
                            </li>
                            <li>
                                Free <Link to="/features/save-the-date">digital save the dates</Link> and{' '}
                                <Link to="/features/day-schedule">shareable day schedule</Link>
                            </li>
                            <li>
                                Guided <Link to="/features/checklist">planning checklist</Link> based on a UK
                                wedding timeline
                            </li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'Zola',
                body: (
                    <>
                        <p>
                            <strong>Overview:</strong> Integrated registry, guest list, website and
                            checklists — a strong all-rounder, though built primarily for the US market.
                        </p>
                        <p>
                            <strong>Key features:</strong>
                        </p>
                        <ul>
                            <li>All-in-one registry &amp; website</li>
                            <li>Real-time budget tracking</li>
                            <li>Vetted vendor marketplace</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'The Knot',
                body: (
                    <>
                        <p>
                            <strong>Overview:</strong> Free app with advanced floorplans and guest
                            management, again with a largely US focus.
                        </p>
                        <p>
                            <strong>Key features:</strong>
                        </p>
                        <ul>
                            <li>Interactive seating charts</li>
                            <li>SEO-friendly website templates</li>
                            <li>Vendor reviews &amp; messaging</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'Bridebook',
                body: (
                    <>
                        <p>
                            <strong>Overview:</strong> UK-founded planning app with AI-driven checklists and
                            budget tools.
                        </p>
                        <p>
                            <strong>Key features:</strong>
                        </p>
                        <ul>
                            <li>Personalised to-do lists</li>
                            <li>Budget calculators</li>
                            <li>Desktop &amp; mobile sync</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'Hitched',
                body: (
                    <>
                        <p>
                            <strong>Overview:</strong> Best known for local vendor discovery and inspiration
                            galleries in the UK.
                        </p>
                        <p>
                            <strong>Key features:</strong>
                        </p>
                        <ul>
                            <li>Location-based search</li>
                            <li>Curated inspiration</li>
                            <li>Real-time vendor chat</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'The verdict',
                body: (
                    <p>
                        If you want registry-first planning for a US wedding, Zola and The Knot are strong.
                        For UK couples who want guest list, RSVPs, website, seating and schedule genuinely
                        working together in one app — without juggling spreadsheets — First Dance is built
                        for exactly that, and the core tools are free to start.
                    </p>
                ),
            },
        ],
        cta: {
            heading: 'Ready to streamline your planning?',
            text: 'Try First Dance for free today and keep everything for your big day in one place.',
        },
    },
    {
        slug: 'just-engaged-first-steps',
        title: 'Just Engaged? 10 First Steps to Plan Your UK Wedding',
        seoTitle: 'Just Engaged? 10 First Steps to Plan Your UK Wedding | First Dance',
        seoDescription:
            'Just got engaged? Here are the 10 first steps UK couples should take, from setting a budget and rough guest list to choosing a date and getting organised.',
        excerpt:
            'The ring is on, the prosecco is flowing — now what? Here’s exactly where to start, without the overwhelm.',
        date: '2026-06-05',
        dateLabel: '5 June 2026',
        readTime: '6 min read',
        image: imgCouplePlanning,
        imageAlt: 'Newly engaged UK couple starting to plan their wedding together',
        intro:
            'Congratulations — you’re engaged! Once the celebrations settle, most couples hit the same question: where on earth do we start? The good news is that early wedding planning is mostly about a handful of big, fun decisions. Here are the ten first steps we recommend to every newly engaged UK couple.',
        sections: [
            {
                heading: '1. Enjoy being engaged first',
                body: (
                    <p>
                        Seriously. Take a couple of weeks to celebrate before opening a single spreadsheet.
                        Tell your favourite people in person, take too many ring photos, and let it sink in.
                        The average UK engagement lasts well over a year — you have time.
                    </p>
                ),
            },
            {
                heading: '2. Insure the ring',
                body: (
                    <p>
                        Not romantic, but important. Add the ring to your home contents insurance or take out
                        a specialist policy, and get it valued if it wasn’t bought new. Do this before it
                        leaves the house for every “let me see it!” dinner.
                    </p>
                ),
            },
            {
                heading: '3. Dream before you plan',
                body: (
                    <p>
                        Before any budgets or venues, sit down together and talk about the day you actually
                        want. Big or intimate? Church, barn, city hotel or registry office? Summer garden
                        party or cosy winter candlelight? Agreeing the feel of the day first makes every later
                        decision easier.
                    </p>
                ),
            },
            {
                heading: '4. Set a realistic budget',
                body: (
                    <p>
                        The average UK wedding now costs north of £20,000, but plenty of brilliant weddings
                        happen for far less. Work out what you can save each month, whether family are
                        contributing, and set a ceiling you’re both comfortable with. Your budget shapes your
                        guest numbers and venue shortlist, so it comes before both.
                    </p>
                ),
            },
            {
                heading: '5. Sketch a rough guest list',
                body: (
                    <p>
                        You don’t need final numbers yet — just a realistic range. Venues price and plan
                        around headcount, so knowing whether you’re a 60-guest wedding or a 150-guest wedding
                        changes everything. Start listing names in the{' '}
                        <Link to="/features/guest-list">First Dance guest list</Link> now and refine as you
                        go; you’ll use the same list later for save the dates, RSVPs and your table plan.
                    </p>
                ),
            },
            {
                heading: '6. Choose a season, then a date',
                body: (
                    <p>
                        Popular UK venues book up 12–18 months ahead for summer Saturdays. If you’re flexible
                        — a Friday, a Sunday, or an off-peak month — you’ll have more choice and often better
                        prices. Pick a season first, hold a couple of candidate dates, and confirm once your
                        venue is booked.
                    </p>
                ),
            },
            {
                heading: '7. Start the venue search early',
                body: (
                    <p>
                        The venue is usually the biggest single spend and the anchor for everything else —
                        date, numbers, catering, even your colour scheme. Shortlist a handful, visit in
                        person, and ask what’s included before you fall in love with anywhere.
                    </p>
                ),
            },
            {
                heading: '8. Get organised in one place',
                body: (
                    <p>
                        This is where most couples drown in screenshots, notes apps and seventeen
                        spreadsheets. Pick one home for your planning early. First Dance keeps your{' '}
                        <Link to="/features/guest-list">guest list</Link>,{' '}
                        <Link to="/features/checklist">checklist</Link>,{' '}
                        <Link to="/features/day-schedule">day schedule</Link> and{' '}
                        <Link to="/features/table-planner">table plan</Link> together in one free app — made
                        for UK couples, and genuinely fun to use.
                    </p>
                ),
            },
            {
                heading: '9. Send digital save the dates',
                body: (
                    <p>
                        Once your date and venue are locked in, let guests know — especially if you’re
                        marrying in peak season or people need to travel. You can send beautiful{' '}
                        <Link to="/features/save-the-date">digital save the dates</Link> over WhatsApp in
                        minutes, completely free, with your formal invitations to follow later.
                    </p>
                ),
            },
            {
                heading: '10. Work through a proper checklist',
                body: (
                    <p>
                        From here, planning is a series of small jobs done in roughly the right order. A
                        guided <Link to="/features/checklist">wedding planning checklist</Link> based on a
                        typical UK timeline tells you what to focus on next — whether you’ve got two years or
                        six months. Tick things off, feel the momentum, and enjoy the journey.
                    </p>
                ),
            },
        ],
        cta: {
            heading: 'Start your planning journey today',
            text: 'Download First Dance free and turn “where do we start?” into “look how much we’ve done”.',
        },
    },
    {
        slug: 'wedding-planning-timeline-uk',
        title: 'Wedding Planning Timeline UK: A Month-by-Month Guide',
        seoTitle: 'Wedding Planning Timeline UK | Month-by-Month Guide | First Dance',
        seoDescription:
            'A complete UK wedding planning timeline: what to book and when, from 12+ months out to the big day itself. Follow the month-by-month guide and stay stress-free.',
        excerpt:
            'What should you actually be doing 12 months out? 6 months? The week before? Here’s the full UK timeline.',
        date: '2026-06-19',
        dateLabel: '19 June 2026',
        readTime: '7 min read',
        image: imgSchedule,
        imageAlt: 'Wedding day timeline planning in the First Dance app',
        intro:
            'Every wedding is different, but the order of jobs is surprisingly consistent. This month-by-month timeline is based on a typical 12–18 month UK engagement — if you have less time, simply compress the early stages and prioritise venue, registrar and photographer first.',
        sections: [
            {
                heading: '12+ months to go: the big rocks',
                body: (
                    <>
                        <p>
                            Everything else hangs off three decisions: budget, guest numbers and venue. Set
                            your budget, sketch your{' '}
                            <Link to="/features/guest-list">guest list</Link>, and book your venue and
                            registrar or church. If a specific photographer, band or celebrant matters to you,
                            enquire now — the best ones book over a year out.
                        </p>
                        <ul>
                            <li>Agree your total budget and who’s contributing</li>
                            <li>Draft your guest list (day vs evening)</li>
                            <li>Book venue, then give notice / book your ceremony</li>
                            <li>Book photographer and any must-have suppliers</li>
                            <li>Take out wedding insurance</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: '9–12 months: lock in the look and the people',
                body: (
                    <>
                        <p>
                            With the date secured, send{' '}
                            <Link to="/features/save-the-date">save the dates</Link> so nobody books a
                            holiday over your weekend. Then start the fun bit — dresses, suits and styling.
                        </p>
                        <ul>
                            <li>Send digital save the dates</li>
                            <li>Start dress shopping (made-to-order dresses take 6–9 months)</li>
                            <li>Choose your wedding party</li>
                            <li>Book caterer, florist, band or DJ</li>
                            <li>Set up your <Link to="/features/wedding-website">wedding website</Link></li>
                        </ul>
                    </>
                ),
            },
            {
                heading: '6–9 months: the details take shape',
                body: (
                    <>
                        <ul>
                            <li>Book honeymoon (check passports!)</li>
                            <li>Order bridesmaid dresses and suits</li>
                            <li>Arrange transport and accommodation blocks</li>
                            <li>Plan your <Link to="/features/menu-planner">menu</Link> with your caterer</li>
                            <li>Book hair and makeup trials</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: '3–6 months: invitations and RSVPs',
                body: (
                    <>
                        <p>
                            Invitations usually go out 3–4 months before the day. Digital{' '}
                            <Link to="/features/rsvp">RSVPs</Link> make this stage dramatically easier —
                            guests reply from their phone, choose their meals as they respond, and your
                            numbers update automatically. No chasing reply cards.
                        </p>
                        <ul>
                            <li>Send invitations with an RSVP deadline (6–8 weeks before)</li>
                            <li>Finalise ceremony details, readings and music</li>
                            <li>Order the cake</li>
                            <li>Buy wedding rings</li>
                            <li>First dress fitting</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: '1–3 months: final numbers and the table plan',
                body: (
                    <>
                        <p>
                            As RSVPs land, the last big puzzle appears: who sits where. A visual{' '}
                            <Link to="/features/table-planner">table planner</Link> that pulls from your
                            confirmed guest list saves hours here — drag tables around, seat guests, and
                            shuffle until it feels right.
                        </p>
                        <ul>
                            <li>Chase outstanding RSVPs and confirm final numbers</li>
                            <li>Build your seating plan</li>
                            <li>Send final meal counts and dietary notes to your caterer</li>
                            <li>Write speeches and vows</li>
                            <li>Final dress fitting</li>
                        </ul>
                    </>
                ),
            },
            {
                heading: 'The final month: hand the plan over',
                body: (
                    <>
                        <p>
                            Build your <Link to="/features/day-schedule">schedule of the day</Link> — from
                            hair and makeup to the last dance — and share the link with your venue,
                            photographer and wedding party so nobody has to ask “what time is…?”. Then
                            delegate everything you can. Your only job on the day is to enjoy it.
                        </p>
                        <ul>
                            <li>Share your day schedule with suppliers and guests</li>
                            <li>Confirm timings with every supplier</li>
                            <li>Prepare payments and tips in labelled envelopes</li>
                            <li>Pack for the honeymoon</li>
                            <li>Print your website QR code for <Link to="/features/guest-photos">guest photo uploads</Link></li>
                        </ul>
                    </>
                ),
            },
        ],
        cta: {
            heading: 'Let the app keep track for you',
            text: 'First Dance has this whole timeline built in as a guided checklist — free to download, lovely to tick off.',
        },
    },
    {
        slug: 'wedding-guest-list-tips',
        title: 'How to Plan Your Wedding Guest List (Without the Drama)',
        seoTitle: 'Wedding Guest List Tips UK | Plan It Without the Drama | First Dance',
        seoDescription:
            'Struggling with your wedding guest list? Practical UK advice on day vs evening guests, plus-ones, children, and tracking RSVPs — without any family drama.',
        excerpt:
            'Day guests, evening guests, plus-ones and the children question — how to settle your guest list and keep the peace.',
        date: '2026-07-01',
        dateLabel: '1 July 2026',
        readTime: '6 min read',
        image: imgRsvp,
        imageAlt: 'Wedding guest list and RSVP tracking in the First Dance app',
        intro:
            'No part of wedding planning causes more quiet tension than the guest list. It decides your venue, your budget and — let’s be honest — a few family conversations you’d rather avoid. Here’s how UK couples can settle theirs early and painlessly.',
        sections: [
            {
                heading: 'Start big, then trim together',
                body: (
                    <p>
                        Begin with the everyone-we-could-possibly-invite list — both of you, separately, then
                        combined. It will be too long, and that’s fine. Now sort it into tiers: people the
                        day wouldn’t feel right without, people you’d love to have, and people you’d invite
                        out of obligation. Your venue capacity and budget then tell you where the line falls,
                        which is much easier than debating names one by one.
                    </p>
                ),
            },
            {
                heading: 'Use day and evening invitations wisely',
                body: (
                    <p>
                        The great British compromise: a smaller ceremony and wedding breakfast, then a bigger
                        evening party. Evening invitations let you include colleagues, neighbours and the
                        wider circle without paying for another forty dinners. In{' '}
                        <Link to="/features/guest-list">First Dance</Link> you mark each guest as all-day or
                        evening, and everything downstream — meal choices, the RSVP flow, your table plan —
                        adapts automatically.
                    </p>
                ),
            },
            {
                heading: 'Decide your plus-one policy once',
                body: (
                    <p>
                        Pick a rule and apply it to everyone: partners in long-term relationships yes, true
                        plus-ones for the wedding party and guests who won’t know anyone else. A single
                        consistent policy is easy to defend; case-by-case decisions are how feelings get
                        hurt. Track plus-ones against each guest so your numbers stay accurate.
                    </p>
                ),
            },
            {
                heading: 'The children question',
                body: (
                    <p>
                        Whatever you choose — all children, family children only, or an adults-only day —
                        make it clear on the invitation and stay consistent. If you are hosting little ones,
                        note ages and who needs a high chair against each guest, and let your caterer know
                        about children’s meals early.
                    </p>
                ),
            },
            {
                heading: 'Track RSVPs properly (not in your head)',
                body: (
                    <p>
                        The list is only half the job — the other half is knowing who’s actually coming.
                        With <Link to="/features/rsvp">digital RSVPs</Link>, each guest gets a personal link
                        over WhatsApp, replies in under a minute, and picks their meals as they respond. Your
                        dashboard shows accepted, declined and awaiting at a glance, so “final numbers” stops
                        being a spreadsheet all-nighter.
                    </p>
                ),
            },
            {
                heading: 'Let the guest list do the rest of the work',
                body: (
                    <p>
                        A well-kept guest list pays for itself three times over: it becomes your{' '}
                        <Link to="/features/save-the-date">save the date</Link> send list, your RSVP tracker,
                        your caterer’s <Link to="/features/menu-planner">meal report</Link> and your{' '}
                        <Link to="/features/table-planner">seating plan</Link>. Add each guest once, and
                        never type their name again.
                    </p>
                ),
            },
        ],
        cta: {
            heading: 'Sort your guest list this weekend',
            text: 'Download First Dance free, add your guests once, and let the app handle RSVPs, meals and seating.',
        },
    },
]

export default BLOG_POSTS

export const getBlogPostBySlug = (slug) =>
    BLOG_POSTS.find((post) => post.slug === slug) || null
